/* ============================================================================
   Despensa. Traduce la línea de pobreza a una lista de compra concreta.

   Para un banco de alimentos la pregunta operativa no es «cuánto vale la canasta»
   sino «cuánto cuesta hoy la despensa que entregamos, y cuánto costaba antes».
   Esta vista arma el desglose oficial escalado al tamaño del hogar y al número de
   días, y contrasta el total contra el valor oficial de la canasta para que se vea
   que no hay magia: es la misma cifra, repartida.
   ========================================================================== */

import { useMemo, useState } from 'react'
import { LineChart } from '../charts/LineChart'
import { Segmented } from '../components/Segmented'
import { Callout, Card, EmptyState, FilterBar, FilterField, Section, Select } from '../components/ui'
import { Delta } from '../components/Delta'
import {
  AMBITO_COLOR,
  AMBITO_LABEL,
  AMBITO_LABEL_F,
  AMBITO_OPTIONS,
  DEFAULT_HOUSEHOLD_SIZE,
  canastaAlimentaria,
  dataset,
  series,
  type Ambito,
} from '../data/dataset'
import { CANASTA_DAYS_PER_MONTH, buildDespensa, changeOver, sliceRange } from '../data/derive'
import { CATEGORY_LABEL, type ProductCategory } from '../data/schema'
import { currency, formatPeriodTitle, number, percent, pluralize } from '../lib/format'

const PEOPLE_OPTIONS = [1, 2, 3, 4, 5, 6, 8].map((n) => ({
  value: String(n),
  label: n === 1 ? '1 persona' : `${n} personas`,
}))

const PERIOD_OPTIONS = [
  { value: '7', label: 'Una semana' },
  { value: '15', label: 'Una quincena' },
  { value: '30', label: 'Un mes' },
] as const

export function Despensa({
  ambito,
  onAmbitoChange,
}: {
  ambito: Ambito
  onAmbitoChange: (value: Ambito) => void
}) {
  const [people, setPeople] = useState(String(DEFAULT_HOUSEHOLD_SIZE))
  const [days, setDays] = useState<'7' | '15' | '30'>('30')

  const basket = series(canastaAlimentaria(ambito))

  /*
     El desglose por producto viene de los boletines PDF y el valor de la canasta
     del Banco de Indicadores: son dos fuentes y cualquiera puede ir por delante.
     La despensa se ancla en el último mes que EXISTE EN LAS DOS, para que el
     total, sus deltas y el contraste contra la cifra oficial hablen del mismo
     mes. Anclarse sólo en el desglose dejaba un titular de julio con un delta
     calculado sobre junio y sin poder verificar la suma.
  */
  const periodoComun = useMemo(() => {
    const enCanasta = new Set(basket.points.map((p) => p.period))
    const delDesglose = dataset.productSeries
      .filter((s) => s.geography === ambito && s.kind === 'valor-mensual')
      .flatMap((s) => s.points.map((p) => p.period))
    const comunes = [...new Set(delDesglose)].filter((p) => enCanasta.has(p)).sort()
    return comunes.length > 0 ? comunes[comunes.length - 1]! : null
  }, [basket.points, ambito])

  const despensa = useMemo(
    () => buildDespensa(dataset, ambito, Number(people), Number(days), periodoComun, basket),
    [ambito, people, days, periodoComun, basket],
  )

  const byCategory = useMemo(() => {
    const map = new Map<ProductCategory, number>()
    for (const line of despensa.lines) {
      map.set(line.product.category, (map.get(line.product.category) ?? 0) + line.cost)
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1])
  }, [despensa])

  const factor = (Number(people) * Number(days)) / CANASTA_DAYS_PER_MONTH
  const scaled = useMemo(
    () => ({
      ...basket,
      points: sliceRange(basket.points, '60m').map((p) => ({ ...p, value: p.value * factor })),
    }),
    [basket, factor],
  )

  /* Recortar con <= no basta: si el desglose va por delante, el filtro no quita
     nada y `changeOver` ancla su extremo en el último mes de la canasta, no en el
     de la despensa. Con `periodoComun` los dos extremos coinciden por
     construcción, y aquí se exige explícitamente. */
  const basketUpToPeriod = useMemo(
    () => basket.points.filter((p) => p.period <= despensa.period),
    [basket.points, despensa.period],
  )
  const anclado = basketUpToPeriod[basketUpToPeriod.length - 1]?.period === despensa.period
  const annual = anclado ? changeOver(basketUpToPeriod, 12) : null
  const monthly = anclado ? changeOver(basketUpToPeriod, 1) : null

  const difference =
    despensa.officialTotal !== null ? despensa.total - despensa.officialTotal : null

  /*
     Sumar 42 valores redondeados a dos decimales y luego escalarlos al hogar
     arrastra centavos. Una tolerancia absoluta fija marcaba como discrepancia
     los 8 centavos de una despensa de 10 mil pesos, así que la tolerancia es
     relativa al total; por encima de eso sí es una inconsistencia de la fuente.
  */
  const isRounding =
    difference !== null &&
    despensa.officialTotal !== null &&
    Math.abs(difference) <= Math.max(0.05, despensa.officialTotal * 0.0005)

  /** El renglón de comida fuera de casa no se puede donar: importa decirlo. */
  const outsideHomeShare = useMemo(
    () =>
      despensa.lines
        .filter((line) => line.nonComparableUnit)
        .reduce((acc, line) => acc + line.share, 0),
    [despensa.lines],
  )

  const periodLabel = PERIOD_OPTIONS.find((option) => option.value === days)?.label.toLowerCase() ?? ''

  /* Sin desglose publicado para este ámbito no hay lista que armar. Antes el
     periodo vacío llegaba a los formateadores e imprimía «Precios de  0.». */
  if (periodoComun === null || despensa.lines.length === 0) {
    return (
      <>
        <header className="view-head">
          <p className="t-micro view-head__eyebrow">Despensa</p>
          <h1 className="t-title1">La canasta convertida en lista de compra</h1>
        </header>
        <FilterBar ariaLabel="Configuración de la despensa">
          <FilterField label="Ámbito">
            <Segmented
              options={AMBITO_OPTIONS}
              value={ambito}
              onChange={onAmbitoChange}
              ariaLabel="Ámbito geográfico"
            />
          </FilterField>
        </FilterBar>
        <Card>
          <EmptyState
            title="Todavía no hay desglose por producto para este ámbito"
            body="El desglose viene de los cuadros del boletín mensual del INEGI. En cuanto haya un mes con desglose y valor de canasta a la vez, la lista aparece aquí."
          />
        </Card>
      </>
    )
  }

  return (
    <>
      <header className="view-head">
        <p className="t-micro view-head__eyebrow">Despensa</p>
        <h1 className="t-title1">La canasta convertida en lista de compra</h1>
        <p className="t-body ink-2">
          El mismo desglose oficial que sostiene la línea de pobreza, escalado al hogar y al periodo
          que elija. Los montos por renglón son los que publica el INEGI, no una estimación; abajo se
          contrasta la suma contra el valor oficial de la canasta.
        </p>
      </header>

      <FilterBar ariaLabel="Configuración de la despensa">
        <FilterField label="Ámbito">
          <Segmented
            options={AMBITO_OPTIONS}
            value={ambito}
            onChange={onAmbitoChange}
            ariaLabel="Ámbito geográfico" />
        </FilterField>
        <FilterField label="Integrantes del hogar">
          <Select value={people} onChange={setPeople} options={PEOPLE_OPTIONS} ariaLabel="Integrantes" />
        </FilterField>
        <FilterField label="Periodo">
          <Segmented
            options={PERIOD_OPTIONS}
            value={days}
            onChange={setDays}
            ariaLabel="Periodo de la despensa" />
        </FilterField>
      </FilterBar>

      <div className="grid grid--split">
        <Card>
          <div className="stack">
            <div className="stack stack--tight">
              <p className="t-footnote ink-2">
                Costo de la despensa para {people} {Number(people) === 1 ? 'persona' : 'personas'} ·{' '}
                {periodLabel} · ámbito {AMBITO_LABEL[ambito]}
              </p>
              <p className="t-hero despensa__total">{currency(despensa.total)}</p>
              <div className="row">
                {annual ? <Delta value={annual.percent} size="md" since="vs. hace un año" /> : null}
                {monthly ? <Delta value={monthly.percent} since="vs. el mes anterior" /> : null}
              </div>
              <p className="t-caption ink-3">
                Precios de {formatPeriodTitle(despensa.period).toLowerCase()}.{' '}
                {difference === null
                  ? 'El valor oficial de la canasta para este mes todavía no está publicado, así que la suma no se pudo contrastar.'
                  : isRounding
                    ? `La suma de los renglones reproduce el valor oficial de la canasta${
                        Math.abs(difference) >= 0.005
                          ? `; los ${currency(Math.abs(difference))} de diferencia son redondeo de ${despensa.lines.length} renglones`
                          : ''
                      }.`
                    : `La suma de renglones difiere ${currency(Math.abs(difference))} del valor oficial escalado. Vea la nota de la fuente en Fuentes.`}
              </p>
            </div>

            <div className="stack stack--tight">
              <p className="t-micro ink-3">Reparto por categoría</p>
              <ul className="cat-list">
                {byCategory.map(([category, cost]) => {
                  const share = despensa.total > 0 ? (cost / despensa.total) * 100 : 0
                  return (
                    <li key={category} className="cat-list__item">
                      <span className="cat-list__label t-footnote">{CATEGORY_LABEL[category]}</span>
                      <span className="cat-list__bar" aria-hidden="true">
                        <span
                          className="cat-list__fill"
                          style={{ width: `${Math.max(1.5, share)}%` }}
                        />
                      </span>
                      <span className="cat-list__value t-caption tabular">{currency(cost, 0)}</span>
                      <span className="cat-list__share t-caption ink-3 tabular">
                        {percent(share, 0)}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        </Card>

        <Card>
          <LineChart
            series={[
              {
                id: 'despensa',
                label: 'Costo de esta despensa',
                shortLabel: 'Despensa',
                color: AMBITO_COLOR[ambito],
                points: scaled.points,
              },
            ]}
            title="Cuánto ha costado esta misma despensa"
            subtitle={`Últimos cinco años. Misma composición, ${people} ${Number(people) === 1 ? 'integrante' : 'integrantes'}, ${periodLabel}.`}
            formatValue={(value) => currency(value, 0)}
            unitLabel="pesos por despensa"
            height={280}
            area
          />
        </Card>
      </div>

      <Section
        title="Lista de compra"
        description={`Los ${despensa.lines.length} renglones de la canasta alimentaria ${AMBITO_LABEL_F[ambito]}, ordenados por lo que pesan en el gasto. La cantidad es la que implica el gasto oficial al precio publicado.`}
      >
        <Card>
          <div
            className="scroll-x"
            tabIndex={0}
            role="region"
            aria-label="Lista de compra completa, tabla con scroll horizontal"
          >
            <table className="despensa-table">
              <caption className="sr-only">
                Lista de compra de la despensa, {periodLabel}, {people}{' '}
                {pluralize(Number(people), 'integrante', 'integrantes')}, ámbito{' '}
                {AMBITO_LABEL[ambito]}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Producto</th>
                  <th scope="col">Cantidad</th>
                  <th scope="col">Precio unitario</th>
                  <th scope="col">Costo</th>
                  <th scope="col">Del total</th>
                </tr>
              </thead>
              <tbody>
                {despensa.lines.map((line) => (
                  <tr key={line.product.id}>
                    <th scope="row">
                      <span className="despensa-table__name">{line.product.name}</span>
                      <span className="despensa-table__cat t-caption ink-3">
                        {CATEGORY_LABEL[line.product.category]}
                      </span>
                    </th>
                    <td className="tabular">
                      {line.nonComparableUnit ? (
                        <span className="ink-3">no aplica</span>
                      ) : line.quantity === null ? (
                        <span className="ink-3">sin dato</span>
                      ) : (
                        `${number(line.quantity, line.quantity < 10 ? 2 : 1)} ${line.unit}`
                      )}
                    </td>
                    <td className="tabular">
                      {line.nonComparableUnit ? (
                        <span className="ink-3">por ración</span>
                      ) : line.unitPrice === null ? (
                        <span className="ink-3">sin dato</span>
                      ) : (
                        `${currency(line.unitPrice)} / ${line.unit}`
                      )}
                    </td>
                    <td className="tabular despensa-table__cost">{currency(line.cost)}</td>
                    <td className="tabular ink-2">{percent(line.share, 1)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Total</th>
                  <td />
                  <td />
                  <td className="tabular despensa-table__cost">{currency(despensa.total)}</td>
                  <td className="tabular ink-2">100.0 %</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      </Section>

      {outsideHomeShare > 5 ? (
        <Callout kind="warning" title="Una parte de esta canasta no se puede donar">
          <p>
            El {percent(outsideHomeShare, 0)} del costo de la canasta{' '}
            {AMBITO_LABEL_F[ambito]} está en{' '}
            {despensa.lines
              .filter((line) => line.nonComparableUnit)
              .map((line) => line.product.name.toLowerCase())
              .join(' y ')}
            : renglones que el INEGI publica por ración y no por kilogramo. Son gasto real de los
            hogares mexicanos y por eso están en la medición oficial, pero no corresponden a producto
            que un banco de alimentos pueda rescatar o entregar.
          </p>
          <p>
            Para dimensionar una despensa donable, la referencia útil es el{' '}
            {percent(100 - outsideHomeShare, 0)} restante:{' '}
            <strong>{currency(despensa.total * (1 - outsideHomeShare / 100))}</strong> para{' '}
            {people} {Number(people) === 1 ? 'persona' : 'personas'} en {periodLabel}.
          </p>
        </Callout>
      ) : null}

      <Callout kind="info" title="Qué es y qué no es esta lista">
        <p>
          Es la canasta de referencia con la que México mide la pobreza extrema por ingresos, no una
          recomendación nutricional ni la despensa que entrega un banco de alimentos. Cubre el
          requerimiento de energía de una persona promedio; no está balanceada por grupos de alimentos
          ni ajustada por edad.
        </p>
        <p>
          Las cantidades son <strong>implícitas</strong>: se obtienen dividiendo el gasto oficial de
          cada renglón entre su precio publicado. Sirven para dimensionar una compra, no como gramaje
          normativo. Dos renglones se publican por ración y no tienen cantidad comparable.
        </p>
      </Callout>
    </>
  )
}
